/***
 *
 * @file client/foundations/OneTrust/index.tsx
 * @description OneTrust client component will be used for rendering the content.
 * @author Ayesha Kumar
 * @organization Truminds Software System
 * @since 13 Jan 2026
 *
 */
'use client'

import Script from "next/script";
// import "./OneTrust.scss";
import { useEffect } from "react";
import { usePathname } from "next/navigation";

type AppConfig = {
  oneTrust?: { scriptId?: string };
};

type Props = {
  scriptId: string
};

const OneTrust = ({...props}: Props) => {
  const { scriptId } = props;
  const pathName = usePathname();

  useEffect(() => {
    // console.log("OneTrust ---------", scriptId)
    // Only load if user hasn't dismissed cookie notice
    const cookieNotice = localStorage.getItem('onetrust-dismissed');
    if (!cookieNotice && scriptId) {
      // Initialize dataLayer after hydration
      (window as any).dataLayer = (window as any).dataLayer || [];
    }
  }, [scriptId]);

  useEffect(() => {
    const handleRouteUpdate = () => {
      if (!window.OneTrust) return;

      // 1. Tell OneTrust the route changed
      window.OneTrust.Init();

      // 2. Re-bind your custom footer link
      // OneTrust uses '.ot-sdk-show-settings' by default for custom links
      const footerLinks = document.querySelectorAll(".ot-sdk-show-settings");
      
      const openPreferenceCenter = (e: Event) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        // Force the Preference Center to open
        if (window.OneTrust && typeof window.OneTrust.ToggleInfoDisplay === 'function') {
          window.OneTrust.ToggleInfoDisplay();
        }
      };

      footerLinks.forEach((link) => {
        // Cleanup first to avoid duplicate listeners
        link.removeEventListener("click", openPreferenceCenter);
        link.addEventListener("click", openPreferenceCenter);
      });
    };

    // Give Next.js a moment to render the new footer before searching for the link
    const timer = setTimeout(handleRouteUpdate, 1000);

    return () => clearTimeout(timer);
  }, [pathName]);

  return (
    scriptId ? (
      <>
        {/* Default Consent Settings */}
        <Script id="consent-default" strategy="lazyOnload">
          {/* gtag('set', 'developer_id.<ID>', true); */}
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('consent', 'default', {
              'ad_storage': 'denied',
              'ad_user_data': 'denied',
              'ad_personalization': 'denied',
              'analytics_storage': 'denied',
              'functionality_storage': 'granted',
              'security_storage': 'granted'
            });
          `}
        </Script>

        {/* OneTrust Loader */}
        <Script
          id="onetrust"
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          data-domain-script={scriptId}
          data-document-language="true"
          strategy="lazyOnload"
        />

        {/* OneTrust Consent Update */}
        <Script id="onetrust-wrapper" strategy="lazyOnload">
          {`
            function OptanonWrapper() {
              if (typeof OnetrustActiveGroups !== 'undefined') {
                gtag('consent', 'update', {
                  'ad_storage': OnetrustActiveGroups.indexOf('4') === -1 ? 'denied' : 'granted',
                  'ad_user_data': OnetrustActiveGroups.indexOf('4') === -1 ? 'denied' : 'granted',
                  'ad_personalization': OnetrustActiveGroups.indexOf('4') === -1 ? 'denied' : 'granted',
                  'analytics_storage': OnetrustActiveGroups.indexOf('2') === -1 ? 'denied' : 'granted'
                });
              }
            }
          `}
        </Script>
      </>
    ) : null
  );
};

export default OneTrust;